import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import doctorTeleConsultationApi from "./doctorTeleConsultationApi";
import { TeleRTCClient } from "../tele-consultation/webrtcservice";
import "./DoctorTeleConsultationPage.css";

const TERMINAL_STATUSES = ["COMPLETED", "CANCELLED"];
const ABNORMAL_FLAG_PATTERN = /\b(HIGH|LOW|ABNORMAL|CRITICAL|POSITIVE|ELEVATED|DECREASED)\b/;

const getRequestError = (requestError, fallback) =>
	requestError?.response?.data?.message ||
	requestError?.response?.data?.detail ||
	fallback;

const getMediaError = (mediaError) => {
	if (mediaError?.name === "NotAllowedError" || mediaError?.name === "PermissionDeniedError") {
		return "Camera or microphone access was denied. Allow access in your browser settings and reload the consultation.";
	}

	if (mediaError?.name === "NotFoundError" || mediaError?.name === "DevicesNotFoundError") {
		return "No camera or microphone was found. Connect a device and try again.";
	}

	return "Unable to access the camera or microphone. Check your devices and try again.";
};

const formatLabel = (value) =>
	String(value || "")
		.replace(/_/g, " ")
		.replace(/\b\w/g, (character) => character.toUpperCase());

const formatDisplayValue = (value) => {
	if (value === null || value === undefined || value === "") {
		return "-";
	}

	if (typeof value === "boolean") {
		return value ? "Yes" : "No";
	}

	if (Array.isArray(value)) {
		return value.length ? value.map(formatDisplayValue).join(", ") : "-";
	}

	if (typeof value === "object") {
		return Object.entries(value)
			.map(([key, nestedValue]) => `${formatLabel(key)}: ${formatDisplayValue(nestedValue)}`)
			.join(" · ");
	}

	return String(value);
};

const formatDateTime = (value) => {
	if (!value) return "-";

	const date = new Date(value);

	if (Number.isNaN(date.getTime())) return "-";

	return date.toLocaleString(undefined, {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
};

const formatCallDuration = (totalSeconds) => {
	const safeSeconds = Math.max(0, Number(totalSeconds) || 0);
	const hours = Math.floor(safeSeconds / 3600);
	const minutes = Math.floor((safeSeconds % 3600) / 60);
	const seconds = Math.floor(safeSeconds % 60);
	const pad = (value) => String(value).padStart(2, "0");

	return hours > 0
		? `${hours}:${pad(minutes)}:${pad(seconds)}`
		: `${pad(minutes)}:${pad(seconds)}`;
};

const getInitials = (value) => {
	const parts = String(value || "")
		.trim()
		.split(/\s+/)
		.filter(Boolean);

	if (!parts.length) return "P";
	if (parts.length === 1) return parts[0].charAt(0).toUpperCase();

	return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
};

const getTestResultTone = (result) => {
	if (!result) return "neutral";

	if (result.is_abnormal === true || result.abnormal === true) {
		return "danger";
	}

	const status = String(result.status || "").toUpperCase();
	const flag = String(result.flag || result.interpretation || "").toUpperCase();
	const combined = `${status} ${flag}`;

	if (ABNORMAL_FLAG_PATTERN.test(combined)) {
		return "danger";
	}

	if (status === "COMPLETED" || status === "NORMAL") {
		return "success";
	}

	return "neutral";
};

const getUnderwritingValueTone = (label, value) => {
	const textValue = String(value ?? "");

	if (/(heart rate|pulse)/i.test(label)) {
		const numericValue = Number(textValue.replace(/[^\d.-]/g, ""));

		if (textValue !== "-" && Number.isFinite(numericValue) && numericValue > 0) {
			return numericValue < 60 || numericValue > 100 ? "is-flagged" : "is-normal";
		}
	}

	if (ABNORMAL_FLAG_PATTERN.test(textValue.toUpperCase())) {
		return "is-flagged";
	}

	return "";
};

const buildNestedLabel = (parentKey, childKey) => {
	const parentLabel = formatLabel(parentKey);

	if (childKey.startsWith("has_") && formatLabel(childKey.slice(4)) === parentLabel) {
		return parentLabel;
	}

	return `${parentLabel} · ${formatLabel(childKey)}`;
};

const svgProps = {
	width: 20,
	height: 20,
	viewBox: "0 0 24 24",
	fill: "none",
	stroke: "currentColor",
	strokeWidth: 2,
	strokeLinecap: "round",
	strokeLinejoin: "round",
	"aria-hidden": true,
	focusable: "false",
};

const IconMic = () => (
	<svg {...svgProps}>
		<path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
		<path d="M19 10v1a7 7 0 0 1-14 0v-1" />
		<line x1="12" y1="18" x2="12" y2="22" />
	</svg>
);

const IconMicOff = () => (
	<svg {...svgProps}>
		<line x1="2" y1="2" x2="22" y2="22" />
		<path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V5a3 3 0 0 0-5.94-.6" />
		<path d="M19 10v1a7 7 0 0 1-10.91 5.82M5 10v1a7 7 0 0 0 .83 3.32" />
		<line x1="12" y1="18" x2="12" y2="22" />
	</svg>
);

const IconVideo = () => (
	<svg {...svgProps}>
		<path d="M23 7l-7 5 7 5V7z" />
		<rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
	</svg>
);

const IconVideoOff = () => (
	<svg {...svgProps}>
		<line x1="2" y1="2" x2="22" y2="22" />
		<path d="M10.66 5H14a2 2 0 0 1 2 2v2.34l1 1L23 7v10" />
		<path d="M16 16v2a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2" />
	</svg>
);

const IconPhoneOff = () => (
	<svg {...svgProps}>
		<path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.42 19.42 0 0 1-3.33-2.67m-2.67-3.34a19.79 19.79 0 0 1-3.07-8.63A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91" />
		<line x1="23" y1="1" x2="1" y2="23" />
	</svg>
);

const IconClock = () => (
	<svg {...svgProps}>
		<circle cx="12" cy="12" r="10" />
		<polyline points="12 6 12 12 16 14" />
	</svg>
);

const IconCheck = () => (
	<svg {...svgProps}>
		<polyline points="20 6 9 17 4 12" />
	</svg>
);

const IconAlert = () => (
	<svg {...svgProps}>
		<path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
		<line x1="12" y1="9" x2="12" y2="13" />
		<line x1="12" y1="17" x2="12.01" y2="17" />
	</svg>
);

const IconArrowLeft = () => (
	<svg {...svgProps}>
		<line x1="19" y1="12" x2="5" y2="12" />
		<polyline points="12 19 5 12 12 5" />
	</svg>
);

const IconActivity = () => (
	<svg {...svgProps}>
		<polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
	</svg>
);

const IconShield = () => (
	<svg {...svgProps}>
		<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
	</svg>
);

const IconUser = () => (
	<svg {...svgProps}>
		<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
		<circle cx="12" cy="7" r="4" />
	</svg>
);

const IconNotes = () => (
	<svg {...svgProps}>
		<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
		<polyline points="14 2 14 8 20 8" />
		<line x1="8" y1="13" x2="16" y2="13" />
		<line x1="8" y1="17" x2="13" y2="17" />
	</svg>
);

const IconX = () => (
	<svg {...svgProps}>
		<line x1="18" y1="6" x2="6" y2="18" />
		<line x1="6" y1="6" x2="18" y2="18" />
	</svg>
);

const CONSULTATION_TABS = [
	{ id: "notes", label: "Notes", icon: IconNotes },
	{ id: "tests", label: "Test Results", icon: IconActivity },
	{ id: "underwriting", label: "Underwriting", icon: IconShield },
	{ id: "patient", label: "Patient Info", icon: IconUser },
];

const renderUnderwritingGroup = (groupKey, group) => {
	if (!group || typeof group !== "object" || Array.isArray(group)) {
		return null;
	}

	const entries = Object.entries(group).flatMap(([key, value]) => {
		if (value && typeof value === "object" && !Array.isArray(value)) {
			return Object.entries(value).map(([nestedKey, nestedValue]) => ({
				key: `${key}-${nestedKey}`,
				label: buildNestedLabel(key, nestedKey),
				value: formatDisplayValue(nestedValue),
			}));
		}

		return [{
			key,
			label: formatLabel(key),
			value: formatDisplayValue(value),
		}];
	});

	if (!entries.length) {
		return null;
	}

	return (
		<div className="doctor-underwriting-group" key={groupKey}>
			<h4>{formatLabel(groupKey)}</h4>
			<dl className="doctor-underwriting-facts">
				{entries.map((entry) => (
					<div key={entry.key}>
						<dt>{entry.label}</dt>
						<dd className={getUnderwritingValueTone(entry.label, entry.value)}>
							{entry.value}
						</dd>
					</div>
				))}
			</dl>
		</div>
	);
};

const PatientSummaryBar = ({ name, age, gender, memberId, allergies, criticalFlag }) => {
	const allergyText = Array.isArray(allergies)
		? allergies.filter(Boolean).join(", ")
		: String(allergies || "").trim();

	const ageLabel =
		age === null || age === undefined || age === "" || age === "-"
			? "Age not recorded"
			: `${age} yrs`;

	return (
		<section className="doctor-patient-bar" aria-label="Patient summary">
			<div className="doctor-patient-identity">
				<span className="doctor-patient-avatar" aria-hidden="true">
					{getInitials(name)}
				</span>

				<div className="doctor-patient-identity-text">
					<h2>{name}</h2>
					<p>
						<span>{ageLabel}</span>
						<span aria-hidden="true">·</span>
						<span>{gender || "Gender not recorded"}</span>
						<span aria-hidden="true">·</span>
						<span>Member ID {memberId}</span>
					</p>
				</div>
			</div>

			<div className="doctor-patient-flags">
				{allergyText ? (
					<span className="doctor-flag doctor-flag--danger">
						<IconAlert />
						Allergies: {allergyText}
					</span>
				) : (
					<span className="doctor-flag doctor-flag--neutral">
						<IconCheck />
						No known allergies
					</span>
				)}

				{criticalFlag && (
					<span className="doctor-flag doctor-flag--danger">
						<IconAlert />
						Critical flag
					</span>
				)}
			</div>
		</section>
	);
};

const DoctorTeleConsultationPage = () => {
	const { consultationId } = useParams();
	const navigate = useNavigate();

	const [consultation, setConsultation] = useState(null);
	const [loading, setLoading] = useState(true);
	const [starting, setStarting] = useState(false);
	const [ending, setEnding] = useState(false);
	const [joining, setJoining] = useState(false);
	const [error, setError] = useState("");
	const [localStream, setLocalStream] = useState(null);
	const [hasRemoteStream, setHasRemoteStream] = useState(false);
	const [micEnabled, setMicEnabled] = useState(true);
	const [cameraEnabled, setCameraEnabled] = useState(true);
	const [webSocketConnected, setWebSocketConnected] = useState(false);
	const [webRtcConnected, setWebRtcConnected] = useState(false);
	const [reconnecting, setReconnecting] = useState(false);
	const [connectionError, setConnectionError] = useState("");
	const [testResults, setTestResults] = useState([]);
	const [loadingTestResults, setLoadingTestResults] = useState(false);
	const [testResultsError, setTestResultsError] = useState("");
	const [underwriting, setUnderwriting] = useState(null);
	const [loadingUnderwriting, setLoadingUnderwriting] = useState(false);
	const [underwritingError, setUnderwritingError] = useState("");

	// Presentation-only state (tabs, timer, modal, save feedback).
	const [activeTab, setActiveTab] = useState("notes");
	const [showEndModal, setShowEndModal] = useState(false);
	const [elapsedSeconds, setElapsedSeconds] = useState(0);
	const [savedAt, setSavedAt] = useState(null);
	const [savedSnapshot, setSavedSnapshot] = useState("");

	const mountedRef = useRef(false);
	const pollingRef = useRef(null);
	const rtcClientRef = useRef(null);
	const localStreamRef = useRef(null);
	const remoteStreamRef = useRef(null);
	const localVideoRef = useRef(null);
	const remoteVideoRef = useRef(null);
	const joinStartedRef = useRef(false);
	const endModalRef = useRef(null);
	const notesFormRef = useRef(null);
	const pendingSaveRef = useRef(false);
	const consultationWebSocketId = consultation?.consultation_id;
	const roomId = consultation?.room_id;
	const consultationStatus = consultation?.status;

	const fetchConsultation = useCallback(async () => {
		try {
			const response = await doctorTeleConsultationApi.getConsultation(consultationId);
			const data = response?.consultation || response;

			if (!data?.consultation_id) {
				throw new Error("Consultation details were not returned by the server.");
			}

			if (mountedRef.current) {
				setConsultation(data);
				setError("");
			}
			return data;
		} catch (requestError) {
			if (mountedRef.current) {
				setError(
					requestError instanceof Error && !requestError.response
						? requestError.message
						: getRequestError(requestError, "Unable to load this consultation.")
				);
			}
			return null;
		} finally {
			if (mountedRef.current) {
				setLoading(false);
			}
		}
	}, [consultationId]);

	const [consultationForm, setConsultationForm] = useState({
		chiefComplaints: "",
		symptomsDuration: "",
		medicalHistory: "",
		clinicalFindings: "",
		diagnosis: "",
		treatmentPlan: "",
		prescription: "",
		followUp: "",
	});

	const handleFormChange = (event) => {
		const { name, value } = event.target;

		setConsultationForm((previous) => ({
			...previous,
			[name]: value,
		}));

		// Clear old messages when the doctor edits the form.
		setNotesError("");
		setNotesSuccess("");
	};

	const [doctorPatient, setDoctorPatient] = useState(null);
	const [loadingNotes, setLoadingNotes] = useState(false);
	const [savingNotes, setSavingNotes] = useState(false);
	const [notesError, setNotesError] = useState("");
	const [notesSuccess, setNotesSuccess] = useState("");

	const prescriptionToText = (prescription) => {
		if (typeof prescription === "string") {
			return prescription;
		}

		if (!Array.isArray(prescription)) {
			return "";
		}

		return prescription.map((item) => {
			if (typeof item === "string") return item;

			if (item && typeof item === "object") {
				return [
					item.medicine,
					item.dosage,
					item.frequency,
					item.duration,
					item.instructions,
				].filter(Boolean).join(" | ");
			}

			return "";
		}).filter(Boolean).join("\n");
	};

	const stopLocalMedia = useCallback(() => {
		const stream = localStreamRef.current;
		if (stream) {
			stream.getTracks().forEach((track) => track.stop());
		}
		localStreamRef.current = null;
		if (mountedRef.current) {
			setLocalStream(null);
		}
		if (localVideoRef.current) {
			localVideoRef.current.srcObject = null;
		}
	}, []);

	const teardownCall = useCallback(() => {
		joinStartedRef.current = false;
		if (rtcClientRef.current) {
			rtcClientRef.current.close(true);
			rtcClientRef.current = null;
		}
		stopLocalMedia();
		remoteStreamRef.current = null;
		if (remoteVideoRef.current) {
			remoteVideoRef.current.srcObject = null;
		}
		if (mountedRef.current) {
			setHasRemoteStream(false);
			setWebSocketConnected(false);
			setWebRtcConnected(false);
			setReconnecting(false);
		}
	}, [stopLocalMedia]);

	const joinCall = useCallback(async () => {
		if (
			joinStartedRef.current ||
			!consultationWebSocketId ||
			consultationStatus !== "ONGOING"
		) {
			return;
		}

		joinStartedRef.current = true;
		setJoining(true);
		setError("");

		try {
			if (!navigator.mediaDevices?.getUserMedia) {
				throw new Error("Camera and microphone access is not supported by this browser.");
			}

			const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
			if (!mountedRef.current) {
				stream.getTracks().forEach((track) => track.stop());
				return;
			}

			localStreamRef.current = stream;
			setLocalStream(stream);
			setMicEnabled(stream.getAudioTracks().some((track) => track.enabled));
			setCameraEnabled(stream.getVideoTracks().some((track) => track.enabled));

			const client = new TeleRTCClient({
				consultationId: consultationWebSocketId,
				participantType: "DOCTOR",
				polite: false,
				localStream: stream,
				shouldReconnect: () =>
					mountedRef.current && !TERMINAL_STATUSES.includes(consultationStatus),
				callbacks: {
					onWsState: setWebSocketConnected,
					onRtcState: (state) => {
						setWebRtcConnected(state === "connected");
						if (state === "connected") setConnectionError("");
						if (state === "failed") {
							setConnectionError("The media connection failed. Trying to recover...");
						}
					},
					onReconnecting: setReconnecting,
					onRemoteStream: (remoteStream) => {
						remoteStreamRef.current = remoteStream || null;
						if (remoteVideoRef.current) {
							remoteVideoRef.current.srcObject = remoteStream || null;
						}
						setHasRemoteStream(Boolean(remoteStream));
					},
					onRemoteLeft: () => {
						setWebRtcConnected(false);
						setConnectionError("The other participant disconnected from the consultation.");
					},
					onError: (message) => setConnectionError(message),
				},
			});

			rtcClientRef.current = client;
			client.start();
		} catch (mediaOrRtcError) {
			joinStartedRef.current = false;
			stopLocalMedia();
			if (mountedRef.current) {
				setError(
					mediaOrRtcError?.name
						? getMediaError(mediaOrRtcError)
						: mediaOrRtcError.message || "Unable to join the consultation."
				);
			}
		} finally {
			if (mountedRef.current) {
				setJoining(false);
			}
		}
	}, [consultationWebSocketId, consultationStatus, stopLocalMedia]);

	useEffect(() => {
		mountedRef.current = true;
		fetchConsultation();
		return () => {
			mountedRef.current = false;
			if (pollingRef.current) clearInterval(pollingRef.current);
			if (rtcClientRef.current) {
				rtcClientRef.current.close(true);
				rtcClientRef.current = null;
			}
			const stream = localStreamRef.current;
			if (stream) stream.getTracks().forEach((track) => track.stop());
			localStreamRef.current = null;
			remoteStreamRef.current = null;
		};
	}, [fetchConsultation]);

	useEffect(() => {
		if (!consultation || TERMINAL_STATUSES.includes(consultation.status)) {
			if (pollingRef.current) {
				clearInterval(pollingRef.current);
				pollingRef.current = null;
			}
			return undefined;
		}

		if (pollingRef.current) clearInterval(pollingRef.current);
		pollingRef.current = setInterval(fetchConsultation, 5000);

		return () => {
			if (pollingRef.current) {
				clearInterval(pollingRef.current);
				pollingRef.current = null;
			}
		};
	}, [consultation?.status, consultationId, fetchConsultation]);

	useEffect(() => {
		if (consultation?.status === "ONGOING") {
			joinCall();
		} else if (TERMINAL_STATUSES.includes(consultation?.status)) {
			teardownCall();
		}
	}, [consultation?.status, joinCall, teardownCall]);

	useEffect(() => {
		if (localVideoRef.current) {
			localVideoRef.current.srcObject = localStream;
		}
	}, [localStream]);

	// Presentation-only call timer.
	useEffect(() => {
		if (consultation?.status !== "ONGOING") {
			setElapsedSeconds(0);
			return undefined;
		}

		const startedAt = consultation?.started_at
			? new Date(consultation.started_at).getTime()
			: NaN;

		const readElapsed = () =>
			Number.isNaN(startedAt)
				? null
				: Math.max(0, Math.floor((Date.now() - startedAt) / 1000));

		const initialElapsed = readElapsed();
		if (initialElapsed !== null) setElapsedSeconds(initialElapsed);

		const interval = setInterval(() => {
			setElapsedSeconds((previous) => {
				const elapsed = readElapsed();
				return elapsed === null ? previous + 1 : elapsed;
			});
		}, 1000);

		return () => clearInterval(interval);
	}, [consultation?.status, consultation?.started_at]);

	// Saving from another tab first reveals the notes, then validates/submits them.
	useEffect(() => {
		if (activeTab !== "notes" || !pendingSaveRef.current) return;

		pendingSaveRef.current = false;
		notesFormRef.current?.requestSubmit();
	}, [activeTab]);

	// Accessible end-call confirmation: focus trap + Escape to close.
	useEffect(() => {
		if (!showEndModal) return undefined;

		const modal = endModalRef.current;
		const previousActiveElement = document.activeElement;
		const focusableSelector =
			'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

		const focusable = modal ? Array.from(modal.querySelectorAll(focusableSelector)) : [];
		focusable[0]?.focus();

		const handleKeyDown = (event) => {
			if (event.key === "Escape") {
				event.preventDefault();
				setShowEndModal(false);
				return;
			}

			if (event.key !== "Tab" || !modal) return;

			const items = Array.from(modal.querySelectorAll(focusableSelector));
			if (!items.length) return;

			const first = items[0];
			const last = items[items.length - 1];

			if (event.shiftKey && document.activeElement === first) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && document.activeElement === last) {
				event.preventDefault();
				first.focus();
			}
		};

		document.addEventListener("keydown", handleKeyDown);

		return () => {
			document.removeEventListener("keydown", handleKeyDown);
			if (previousActiveElement && typeof previousActiveElement.focus === "function") {
				previousActiveElement.focus();
			}
		};
	}, [showEndModal]);

	useEffect(() => {
		let cancelled = false;

		const loadDoctorForm = async () => {
			if (!consultationId) return;

			setLoadingNotes(true);
			setNotesError("");

			try {
				const data =
					await doctorTeleConsultationApi.getDoctorForm(
						consultationId
					);

				if (cancelled) return;

				setDoctorPatient(data.patient || null);

				const loadedForm = {
					chiefComplaints: data.chief_complaints || "",
					symptomsDuration: data.duration_of_symptoms || "",
					medicalHistory: data.medical_history || "",
					clinicalFindings: data.clinical_findings || "",
					diagnosis: data.diagnosis || "",
					treatmentPlan: data.treatment_plan || "",
					prescription: prescriptionToText(
						data.prescription
					),
					followUp: data.follow_up_instructions || "",
				};

				setConsultationForm(loadedForm);
				setSavedSnapshot(JSON.stringify(loadedForm));
			} catch (requestError) {
				if (!cancelled) {
					setNotesError(
						getRequestError(
							requestError,
							"Unable to load the doctor consultation form."
						)
					);
				}
			} finally {
				if (!cancelled) {
					setLoadingNotes(false);
				}
			}
		};

		loadDoctorForm();

		return () => {
			cancelled = true;
		};
	}, [consultationId]);

	const handleStart = async () => {
		setStarting(true);
		setError("");
		try {
			const response = await doctorTeleConsultationApi.startConsultation(consultationId);
			const data = response?.consultation || response;
			if (mountedRef.current) {
				setConsultation((previous) => ({ ...previous, ...data }));
			}
		} catch (requestError) {
			if (mountedRef.current) {
				setError(getRequestError(requestError, "Unable to start this consultation."));
			}
		} finally {
			if (mountedRef.current) setStarting(false);
		}
	};

	useEffect(() => {
		let cancelled = false;

		const loadTestResults = async () => {
			const memberId = consultation?.member?.id;

			if (!memberId) {
				return;
			}

			setLoadingTestResults(true);
			setTestResultsError("");

			try {
				const data =
					await doctorTeleConsultationApi.getFamilyMemberTestResults(
						memberId
					);

				if (cancelled) return;

				setTestResults(
					Array.isArray(data?.results)
						? data.results
						: []
				);
			} catch (requestError) {
				if (!cancelled) {
					setTestResultsError(
						getRequestError(
							requestError,
							"Unable to load patient test results."
						)
					);
					setTestResults([]);
				}
			} finally {
				if (!cancelled) {
					setLoadingTestResults(false);
				}
			}
		};

		loadTestResults();

		return () => {
			cancelled = true;
		};
	}, [consultation?.member?.id]);

	useEffect(() => {
		let cancelled = false;

		const loadUnderwriting = async () => {
			const memberIdentifier =
				consultation?.member?.member_id || consultation?.member?.id;

			if (!memberIdentifier) {
				return;
			}

			setLoadingUnderwriting(true);
			setUnderwritingError("");

			try {
				const data =
					await doctorTeleConsultationApi.getUnderwritingAssessment(
						memberIdentifier
					);

				if (!cancelled) {
					setUnderwriting(data || null);
				}
			} catch (requestError) {
				if (!cancelled) {
					setUnderwriting(null);
					setUnderwritingError(
						getRequestError(
							requestError,
							"Unable to load the patient's underwriting assessment."
						)
					);
				}
			} finally {
				if (!cancelled) {
					setLoadingUnderwriting(false);
				}
			}
		};

		loadUnderwriting();

		return () => {
			cancelled = true;
		};
	}, [consultation?.member?.member_id, consultation?.member?.id]);

	const handleSaveNotes = async (event) => {
		event.preventDefault();

		setSavingNotes(true);
		setNotesError("");
		setNotesSuccess("");

		try {
			const payload = {
				chief_complaints:
					consultationForm.chiefComplaints.trim(),

				duration_of_symptoms:
					consultationForm.symptomsDuration.trim(),

				medical_history:
					consultationForm.medicalHistory.trim(),

				clinical_findings:
					consultationForm.clinicalFindings.trim(),

				diagnosis:
					consultationForm.diagnosis.trim(),

				treatment_plan:
					consultationForm.treatmentPlan.trim(),

				prescription: consultationForm.prescription.trim()
					? [{
						instructions:
							consultationForm.prescription.trim(),
					}]
					: [],

				follow_up_instructions:
					consultationForm.followUp.trim(),
			};

			const saved =
				await doctorTeleConsultationApi.saveDoctorForm(
					consultationId,
					payload
				);

			setDoctorPatient(saved.patient || doctorPatient);

			const savedForm = {
				chiefComplaints: saved.chief_complaints || "",
				symptomsDuration:
					saved.duration_of_symptoms || "",
				medicalHistory: saved.medical_history || "",
				clinicalFindings: saved.clinical_findings || "",
				diagnosis: saved.diagnosis || "",
				treatmentPlan: saved.treatment_plan || "",
				prescription: prescriptionToText(
					saved.prescription
				),
				followUp: saved.follow_up_instructions || "",
			};

			setConsultationForm(savedForm);
			setSavedSnapshot(JSON.stringify(savedForm));
			setSavedAt(new Date());
			setNotesSuccess("Consultation notes saved successfully.");
		} catch (requestError) {
			setNotesError(
				getRequestError(
					requestError,
					"Unable to save consultation notes."
				)
			);
		} finally {
			setSavingNotes(false);
		}
	};

	const handleEnd = async () => {
		setEnding(true);
		setError("");
		teardownCall();
		try {
			const response = await doctorTeleConsultationApi.endConsultation(consultationId);
			const data = response?.consultation || response;
			if (mountedRef.current) {
				setConsultation((previous) => ({ ...previous, ...data }));
			}
		} catch (requestError) {
			if (mountedRef.current) {
				setError(getRequestError(requestError, "Unable to end this consultation."));
				await fetchConsultation();
			}
		} finally {
			if (mountedRef.current) setEnding(false);
		}
	};

	const handleConfirmEnd = () => {
		setShowEndModal(false);
		handleEnd();
	};

	const toggleTrack = (kind) => {
		const tracks =
			kind === "audio"
				? localStreamRef.current?.getAudioTracks()
				: localStreamRef.current?.getVideoTracks();
		if (!tracks?.length) return;
		const enabled = !tracks.some((track) => track.enabled);
		tracks.forEach((track) => {
			track.enabled = enabled;
		});
		if (kind === "audio") setMicEnabled(enabled);
		else setCameraEnabled(enabled);
	};

	const handleTabKeyDown = (event, index) => {
		const supportedKeys = ["ArrowRight", "ArrowLeft", "Home", "End"];
		if (!supportedKeys.includes(event.key)) return;

		event.preventDefault();

		let nextIndex = index;

		if (event.key === "ArrowRight") {
			nextIndex = (index + 1) % CONSULTATION_TABS.length;
		}
		if (event.key === "ArrowLeft") {
			nextIndex = (index - 1 + CONSULTATION_TABS.length) % CONSULTATION_TABS.length;
		}
		if (event.key === "Home") nextIndex = 0;
		if (event.key === "End") nextIndex = CONSULTATION_TABS.length - 1;

		setActiveTab(CONSULTATION_TABS[nextIndex].id);

		const tabList = event.currentTarget.parentElement;
		const tabButtons = tabList ? tabList.querySelectorAll('[role="tab"]') : [];
		tabButtons[nextIndex]?.focus();
	};

	if (loading) {
		return (
			<main className="doctor-call-page">
				<div className="doctor-page-loading" role="status" aria-live="polite">
					<span className="doctor-page-spinner" aria-hidden="true" />
					<p>Loading consultation…</p>
				</div>
			</main>
		);
	}

	if (!consultation) {
		return (
			<main className="doctor-call-page">
				<div className="doctor-state-card">
					<span className="doctor-state-icon doctor-state-icon--danger" aria-hidden="true">
						<IconAlert />
					</span>
					<h1>Consultation unavailable</h1>
					<p role="alert">{error || "Consultation not found."}</p>
					<button
						className="doctor-button doctor-button--secondary"
						type="button"
						onClick={() => navigate("/doctor/tele-consultations")}
					>
						<IconArrowLeft />
						Back to consultations
					</button>
				</div>
			</main>
		);
	}

	if (consultation.status === "COMPLETED") {
		return (
			<main className="doctor-call-page">
				<div className="doctor-state-card">
					<span className="doctor-state-icon doctor-state-icon--success" aria-hidden="true">
						<IconCheck />
					</span>
					<p className="doctor-eyebrow">Consultation finished</p>
					<h1>Consultation completed</h1>
					<p>
						Patient: <strong>{consultation.member?.name || "Patient"}</strong>
					</p>
					<dl className="doctor-state-details">
						<div>
							<dt>Call duration</dt>
							<dd>{formatCallDuration(consultation.duration_seconds ?? 0)}</dd>
						</div>
						<div>
							<dt>Status</dt>
							<dd>Completed</dd>
						</div>
					</dl>
					<button
						className="doctor-button"
						type="button"
						onClick={() => navigate("/doctor/tele-consultations")}
					>
						Back to consultations
					</button>
				</div>
			</main>
		);
	}

	if (consultation.status === "CANCELLED") {
		return (
			<main className="doctor-call-page">
				<div className="doctor-state-card">
					<span className="doctor-state-icon doctor-state-icon--danger" aria-hidden="true">
						<IconX />
					</span>
					<h1>Consultation cancelled</h1>
					<p role="status">This consultation has been cancelled.</p>
					<button
						className="doctor-button doctor-button--secondary"
						type="button"
						onClick={() => navigate("/doctor/tele-consultations")}
					>
						<IconArrowLeft />
						Back to consultations
					</button>
				</div>
			</main>
		);
	}

	const ongoing = consultation.status === "ONGOING";
	const isDirty = savedSnapshot !== "" && JSON.stringify(consultationForm) !== savedSnapshot;

	const underwritingAssessments = Array.isArray(underwriting?.assessments)
		? underwriting.assessments
		: [];

	const latestAssessment = [...underwritingAssessments].sort((first, second) => {
		const firstDate = new Date(first?.submitted_at || first?.created_at || 0).getTime();
		const secondDate = new Date(second?.submitted_at || second?.created_at || 0).getTime();
		return secondDate - firstDate;
	})[0] || null;

	const questionnaireData =
		latestAssessment?.questionnaire_data &&
		typeof latestAssessment.questionnaire_data === "object"
			? latestAssessment.questionnaire_data
			: {};

	const underwritingVitals =
		latestAssessment?.ai_vital_result?.scan?.data &&
		typeof latestAssessment.ai_vital_result.scan.data === "object"
			? latestAssessment.ai_vital_result.scan.data
			: null;

	const heartRate =
		underwritingVitals?.heart_rate ??
		latestAssessment?.ai_vital_result?.heart_rate ??
		null;

	const heartRateNumeric = Number(heartRate);
	const heartRateTone =
		heartRate && Number.isFinite(heartRateNumeric)
			? heartRateNumeric < 60 || heartRateNumeric > 100
				? "is-flagged"
				: "is-normal"
			: "";

	const patientProfile = underwriting?.family_member || null;

	const underwritingCount =
		underwriting?.count ?? underwritingAssessments.length;

	const patientName =
		doctorPatient?.patient_name ||
		consultation.member?.name ||
		"Patient";

	const memberId =
		doctorPatient?.member_id ||
		consultation.member?.member_id ||
		"-";

	const patientAge =
		doctorPatient?.age ??
		consultation.member?.age ??
		"-";

	const patientGender =
		doctorPatient?.gender ||
		consultation.member?.gender ||
		"-";

	const allergies =
		doctorPatient?.allergies ??
		doctorPatient?.known_allergies ??
		consultation.member?.allergies ??
		consultation.member?.known_allergies ??
		"";

	const criticalFlag = Boolean(
		doctorPatient?.critical_flag ||
		doctorPatient?.is_critical ||
		consultation.member?.critical_flag ||
		consultation.member?.is_critical
	);

	const patientInfoItems = [
		{ label: "Patient name", value: patientName },
		{ label: "Member ID", value: memberId },
		{ label: "Age", value: patientAge },
		{ label: "Gender", value: patientGender },
		{
			label: "Mobile",
			value: doctorPatient?.mobile || consultation.member?.mobile || "-",
		},
		{
			label: "Family ID",
			value: doctorPatient?.family_id || consultation.family?.family_id || "-",
		},
		{ label: "Package", value: consultation.package?.name || "-" },
		{ label: "Service", value: consultation.service?.name || "-" },
		{ label: "Technician", value: consultation.technician?.name || "-" },
		{ label: "Consultation status", value: formatLabel(consultation.status) },
	];

	const savedAtLabel = savedAt
		? savedAt.toLocaleTimeString(undefined, {
			hour: "2-digit",
			minute: "2-digit",
		})
		: "";

	const connectionState = reconnecting
		? { label: "Reconnecting", tone: "warning" }
		: webRtcConnected
			? { label: "Connected", tone: "success" }
			: { label: "Connecting", tone: "pending" };

	const tabCounts = {
		tests: testResults.length,
		underwriting: underwritingCount,
	};

	const formFieldCount = (fieldName) => consultationForm[fieldName]?.length || 0;

	return (
		<main className="doctor-call-page">
			<header className="doctor-page-header">
				<div className="doctor-page-heading">
					<button
						className="doctor-back-button"
						type="button"
						onClick={() => navigate("/doctor/tele-consultations")}
					>
						<IconArrowLeft />
						Back
					</button>

					<div>
						<p className="doctor-eyebrow">Doctor workspace</p>
						<h1>Teleconsultation</h1>
						<p className="doctor-page-subtitle">
							Patient: <strong>{consultation.member?.name || "Patient"}</strong>
							{ongoing && roomId ? <> · Room {roomId}</> : null}
						</p>
					</div>
				</div>

				<span className={`doctor-status-badge is-${ongoing ? "ongoing" : "waiting"}`}>
					<span className="doctor-status-dot" aria-hidden="true" />
					{formatLabel(consultation.status)}
				</span>
			</header>

			{error && (
				<div className="doctor-alert doctor-alert--danger" role="alert" aria-live="assertive">
					<IconAlert />
					<span>{error}</span>
				</div>
			)}

			{!ongoing ? (
				<div className="doctor-waiting-layout">
					<div className="doctor-waiting-main">
						<PatientSummaryBar
							name={patientName}
							age={patientAge}
							gender={patientGender}
							memberId={memberId}
							allergies={allergies}
							criticalFlag={criticalFlag}
						/>

						<section className="doctor-info-card" aria-labelledby="waiting-patient-info">
							<div className="doctor-card-heading">
								<div>
									<h2 id="waiting-patient-info">Patient information</h2>
									<p>Review the patient record before starting the call.</p>
								</div>
							</div>

							<dl className="doctor-info-grid">
								{patientInfoItems.map((item) => (
									<div key={item.label}>
										<dt>{item.label}</dt>
										<dd>{item.value}</dd>
									</div>
								))}
							</dl>
						</section>

						<section className="doctor-info-card" aria-labelledby="waiting-clinical-flags">
							<div className="doctor-card-heading">
								<div>
									<h2 id="waiting-clinical-flags">Clinical flags</h2>
									<p>Important safety information for this consultation.</p>
								</div>
							</div>

							<div className="doctor-flags-list">
								{allergies ? (
									<span className="doctor-flag doctor-flag--danger">
										<IconAlert />
										Allergies: {Array.isArray(allergies) ? allergies.filter(Boolean).join(", ") : allergies}
									</span>
								) : (
									<span className="doctor-flag doctor-flag--neutral">
										<IconCheck />
										No known allergies recorded
									</span>
								)}

								{criticalFlag ? (
									<span className="doctor-flag doctor-flag--danger">
										<IconAlert />
										Critical flag raised for this patient
									</span>
								) : (
									<span className="doctor-flag doctor-flag--neutral">
										<IconCheck />
										No critical flag raised
									</span>
								)}
							</div>
						</section>
					</div>

					<aside className="doctor-waiting-card">
						<span className="doctor-waiting-icon" aria-hidden="true">
							<IconVideo />
						</span>
						<p className="doctor-eyebrow">Ready when you are</p>
						<h2>Patient is waiting</h2>
						<p>
							Starting the consultation requests access to your camera and
							microphone and connects you to the patient.
						</p>
						<button
							className="doctor-button doctor-button--lg"
							type="button"
							onClick={handleStart}
							disabled={starting}
						>
							{starting ? (
								<>
									<span className="doctor-button-spinner" aria-hidden="true" />
									Starting…
								</>
							) : (
								<>
									<IconVideo />
									Start consultation
								</>
							)}
						</button>
					</aside>
				</div>
			) : (
				<section className="doctor-live-layout">
					<div className="doctor-live-sidebar">
						<PatientSummaryBar
							name={patientName}
							age={patientAge}
							gender={patientGender}
							memberId={memberId}
							allergies={allergies}
							criticalFlag={criticalFlag}
						/>

						<div className="doctor-video-panel">
							<div className="doctor-video-header">
								<div>
									<h2>Live consultation</h2>
									<p>
										{consultation.member?.name || "Patient"}
										{" · "}
										{consultation.member?.member_id || "-"}
									</p>
								</div>

								<span
									className={`doctor-connection-badge is-${connectionState.tone}`}
									role="status"
									aria-live="polite"
									title={
										webSocketConnected
											? "Signaling connected"
											: "Connecting to signaling server"
									}
								>
									<span className="doctor-connection-dot" aria-hidden="true" />
									{connectionState.label}
								</span>
							</div>

							<div className="doctor-video-stage">
								<video
									ref={remoteVideoRef}
									autoPlay
									playsInline
									className="doctor-remote-video"
								/>

								{!hasRemoteStream && (
									<div className="doctor-video-placeholder">
										<span className="doctor-video-pulse" aria-hidden="true">
											<IconVideo />
										</span>
										<strong>
											{reconnecting
												? "Reconnecting to patient…"
												: "Waiting for patient video…"}
										</strong>
										<span>
											The video will appear here as soon as the patient joins.
										</span>
									</div>
								)}

								<div className="doctor-video-overlay-top">
									<span
										className="doctor-call-timer"
										aria-label={`Call duration ${formatCallDuration(elapsedSeconds)}`}
									>
										<IconClock />
										{formatCallDuration(elapsedSeconds)}
									</span>

									<span className="doctor-room-chip">
										Room {consultation.room_id || "Unavailable"}
									</span>
								</div>

								<div className="doctor-local-video-wrap">
									<video
										ref={localVideoRef}
										autoPlay
										playsInline
										muted
										className="doctor-local-video"
									/>

									{!cameraEnabled && (
										<span className="doctor-camera-off">
											<IconVideoOff />
											Camera off
										</span>
									)}

									<span className="doctor-local-video-label">You</span>
								</div>
							</div>

							{connectionError && (
								<div className="doctor-alert doctor-alert--warning" role="status" aria-live="polite">
									<IconAlert />
									<span>{connectionError}</span>
								</div>
							)}

							{error && !localStream && (
								<div className="doctor-media-retry">
									<p>
										Your camera or microphone is unavailable. Check your
										device permissions, then try again.
									</p>
									<button
										className="doctor-button doctor-button--secondary"
										type="button"
										onClick={joinCall}
										disabled={joining}
									>
										{joining ? (
											<>
												<span className="doctor-button-spinner" aria-hidden="true" />
												Connecting…
											</>
										) : (
											"Retry camera & microphone"
										)}
									</button>
								</div>
							)}

							<div className="doctor-call-controls" role="group" aria-label="Call controls">
								<div className="doctor-control">
									<button
										className={`doctor-control-button ${micEnabled ? "" : "is-off"}`}
										type="button"
										onClick={() => toggleTrack("audio")}
										disabled={!localStream}
										aria-pressed={micEnabled}
										aria-label={micEnabled ? "Mute microphone" : "Unmute microphone"}
										data-tooltip={micEnabled ? "Mute" : "Unmute"}
									>
										{micEnabled ? <IconMic /> : <IconMicOff />}
									</button>
									<span className="doctor-control-caption" aria-hidden="true">
										{micEnabled ? "Mute" : "Unmute"}
									</span>
								</div>

								<div className="doctor-control">
									<button
										className={`doctor-control-button ${cameraEnabled ? "" : "is-off"}`}
										type="button"
										onClick={() => toggleTrack("video")}
										disabled={!localStream}
										aria-pressed={cameraEnabled}
										aria-label={cameraEnabled ? "Turn camera off" : "Turn camera on"}
										data-tooltip={cameraEnabled ? "Camera off" : "Camera on"}
									>
										{cameraEnabled ? <IconVideo /> : <IconVideoOff />}
									</button>
									<span className="doctor-control-caption" aria-hidden="true">
										Camera
									</span>
								</div>

								<div className="doctor-control">
									<button
										className="doctor-control-button doctor-control-button--danger"
										type="button"
										onClick={() => setShowEndModal(true)}
										disabled={ending || joining}
										aria-label="End consultation"
										data-tooltip="End consultation"
									>
										<IconPhoneOff />
									</button>
									<span className="doctor-control-caption" aria-hidden="true">
										End
									</span>
								</div>
							</div>

							{joining && (
								<p className="doctor-joining-message" role="status" aria-live="polite">
									<span className="doctor-spinner-sm" aria-hidden="true" />
									Requesting camera and microphone access…
								</p>
							)}
						</div>
					</div>

					<div className="doctor-notes-panel">
						<div
							className="doctor-tabs"
							role="tablist"
							aria-label="Consultation workspace"
						>
							{CONSULTATION_TABS.map((tab, index) => {
								const TabIcon = tab.icon;
								const count = tabCounts[tab.id];

								return (
									<button
										key={tab.id}
										className={`doctor-tab ${activeTab === tab.id ? "is-active" : ""}`}
										type="button"
										role="tab"
										id={`doctor-tab-${tab.id}`}
										aria-selected={activeTab === tab.id}
										aria-controls={`doctor-tabpanel-${tab.id}`}
										tabIndex={activeTab === tab.id ? 0 : -1}
										onClick={() => setActiveTab(tab.id)}
										onKeyDown={(event) => handleTabKeyDown(event, index)}
									>
										<TabIcon />
										<span>{tab.label}</span>

										{count > 0 && (
											<span className="doctor-tab-count">{count}</span>
										)}

										{tab.id === "notes" && isDirty && (
											<span
												className="doctor-tab-dot"
												aria-label="Unsaved changes"
											/>
										)}
									</button>
								);
							})}
						</div>

						<div className="doctor-tab-panels">
							<section
								className="doctor-tab-panel"
								id="doctor-tabpanel-notes"
								role="tabpanel"
								aria-labelledby="doctor-tab-notes"
								hidden={activeTab !== "notes"}
							>
								<div className="doctor-tab-heading">
									<div>
										<h2>Consultation notes</h2>
										<p>Record findings as you speak with the patient.</p>
									</div>

									{isDirty && (
										<span className="doctor-unsaved-chip">
											<span className="doctor-dirty-dot" aria-hidden="true" />
											Unsaved
										</span>
									)}
								</div>

								{loadingNotes && (
									<div className="doctor-inline-loading" role="status" aria-live="polite">
										<span className="doctor-spinner-sm" aria-hidden="true" />
										Loading saved notes…
									</div>
								)}

								<form
									id="doctor-notes-form"
									ref={notesFormRef}
									className="doctor-consultation-form"
									onSubmit={handleSaveNotes}
								>
									<fieldset className="doctor-form-section">
										<legend className="doctor-form-legend">
											<span className="doctor-form-step" aria-hidden="true">1</span>
											<span>Complaint</span>
											<span className="doctor-form-legend-hint">What brought the patient in</span>
										</legend>

										<div className="doctor-form-grid doctor-form-grid--complaint">
											<div className="doctor-form-field">
												<label htmlFor="chiefComplaints">
													Chief complaints
													<span className="doctor-required" aria-hidden="true">*</span>
													<span className="doctor-field-badge doctor-field-badge--required">
														Required
													</span>
												</label>
												<textarea
													id="chiefComplaints"
													name="chiefComplaints"
													value={consultationForm.chiefComplaints}
													onChange={handleFormChange}
													placeholder="Describe the patient's main complaints"
													rows={3}
													required
													aria-describedby="chiefComplaints-hint"
												/>
												<span className="doctor-field-hint" id="chiefComplaints-hint">
													Include onset, severity and associated symptoms ·{" "}
													{formFieldCount("chiefComplaints")} characters
												</span>
											</div>

											<div className="doctor-form-field">
												<label htmlFor="symptomsDuration">
													Duration of symptoms
												</label>
												<input
													id="symptomsDuration"
													name="symptomsDuration"
													value={consultationForm.symptomsDuration}
													onChange={handleFormChange}
													placeholder="e.g. 3 days"
													aria-describedby="symptomsDuration-hint"
												/>
												<span className="doctor-field-hint" id="symptomsDuration-hint">
													Optional · {formFieldCount("symptomsDuration")} characters
												</span>
											</div>
										</div>
									</fieldset>

									<fieldset className="doctor-form-section">
										<legend className="doctor-form-legend">
											<span className="doctor-form-step" aria-hidden="true">2</span>
											<span>Examination</span>
											<span className="doctor-form-legend-hint">History and clinical findings</span>
										</legend>

										<div className="doctor-form-grid">
											<div className="doctor-form-field">
												<label htmlFor="medicalHistory">
													Medical history
												</label>
												<textarea
													id="medicalHistory"
													name="medicalHistory"
													value={consultationForm.medicalHistory}
													onChange={handleFormChange}
													placeholder="Existing conditions, allergies, medication history"
													rows={3}
													aria-describedby="medicalHistory-hint"
												/>
												<span className="doctor-field-hint" id="medicalHistory-hint">
													Optional · {formFieldCount("medicalHistory")} characters
												</span>
											</div>

											<div className="doctor-form-field">
												<label htmlFor="clinicalFindings">
													Clinical findings
												</label>
												<textarea
													id="clinicalFindings"
													name="clinicalFindings"
													value={consultationForm.clinicalFindings}
													onChange={handleFormChange}
													placeholder="Enter examination findings and observations"
													rows={3}
													aria-describedby="clinicalFindings-hint"
												/>
												<span className="doctor-field-hint" id="clinicalFindings-hint">
													Optional · {formFieldCount("clinicalFindings")} characters
												</span>
											</div>
										</div>
									</fieldset>

									<fieldset className="doctor-form-section">
										<legend className="doctor-form-legend">
											<span className="doctor-form-step" aria-hidden="true">3</span>
											<span>Diagnosis</span>
											<span className="doctor-form-legend-hint">Confirmed or provisional</span>
										</legend>

										<div className="doctor-form-grid">
											<div className="doctor-form-field doctor-form-field--full">
												<label htmlFor="diagnosis">Diagnosis</label>
												<textarea
													id="diagnosis"
													name="diagnosis"
													value={consultationForm.diagnosis}
													onChange={handleFormChange}
													placeholder="Enter diagnosis or provisional diagnosis"
													rows={2}
													aria-describedby="diagnosis-hint"
												/>
												<span className="doctor-field-hint" id="diagnosis-hint">
													Optional · {formFieldCount("diagnosis")} characters
												</span>
											</div>
										</div>
									</fieldset>

									<fieldset className="doctor-form-section">
										<legend className="doctor-form-legend">
											<span className="doctor-form-step" aria-hidden="true">4</span>
											<span>Plan</span>
											<span className="doctor-form-legend-hint">Treatment, prescription and follow-up</span>
										</legend>

										<div className="doctor-form-grid">
											<div className="doctor-form-field">
												<label htmlFor="treatmentPlan">Treatment plan</label>
												<textarea
													id="treatmentPlan"
													name="treatmentPlan"
													value={consultationForm.treatmentPlan}
													onChange={handleFormChange}
													placeholder="Recommended treatment and advice"
													rows={3}
													aria-describedby="treatmentPlan-hint"
												/>
												<span className="doctor-field-hint" id="treatmentPlan-hint">
													Optional · {formFieldCount("treatmentPlan")} characters
												</span>
											</div>

											<div className="doctor-form-field">
												<label htmlFor="prescription">Prescription</label>
												<textarea
													id="prescription"
													name="prescription"
													value={consultationForm.prescription}
													onChange={handleFormChange}
													placeholder="Medicine, dosage, frequency and duration"
													rows={3}
													aria-describedby="prescription-hint"
												/>
												<span className="doctor-field-hint" id="prescription-hint">
													Optional · {formFieldCount("prescription")} characters
												</span>
											</div>

											<div className="doctor-form-field doctor-form-field--full">
												<label htmlFor="followUp">Follow-up instructions</label>
												<textarea
													id="followUp"
													name="followUp"
													value={consultationForm.followUp}
													onChange={handleFormChange}
													placeholder="Next visit or referral instructions"
													rows={2}
													aria-describedby="followUp-hint"
												/>
												<span className="doctor-field-hint" id="followUp-hint">
													Optional · {formFieldCount("followUp")} characters
												</span>
											</div>
										</div>
									</fieldset>
								</form>
							</section>

							<section
								className="doctor-tab-panel"
								id="doctor-tabpanel-tests"
								role="tabpanel"
								aria-labelledby="doctor-tab-tests"
								hidden={activeTab !== "tests"}
							>
								<div className="doctor-tab-heading">
									<div>
										<h2>Patient test results</h2>
										<p>Latest health parameters recorded for this patient.</p>
									</div>

									<span className="doctor-count-badge">
										{testResults.length}{" "}
										{testResults.length === 1 ? "result" : "results"}
									</span>
								</div>

								{loadingTestResults && (
									<div className="doctor-skeleton-grid" role="status" aria-live="polite">
										<span className="doctor-visually-hidden">Loading patient test results…</span>
										{[0, 1, 2, 3].map((skeleton) => (
											<div className="doctor-skeleton-card" key={skeleton}>
												<span className="doctor-skeleton-line is-short" />
												<span className="doctor-skeleton-line is-value" />
												<span className="doctor-skeleton-line" />
											</div>
										))}
									</div>
								)}

								{testResultsError && (
									<div className="doctor-alert doctor-alert--danger" role="alert">
										<IconAlert />
										<span>{testResultsError}</span>
									</div>
								)}

								{!loadingTestResults &&
									!testResultsError &&
									testResults.length === 0 && (
										<div className="doctor-empty-state">
											<span className="doctor-empty-state-icon" aria-hidden="true">
												<IconActivity />
											</span>
											<h3>No test results yet</h3>
											<p>
												Results recorded by the technician will appear here
												automatically.
											</p>
										</div>
									)}

								{!loadingTestResults && testResults.length > 0 && (
									<div className="doctor-result-grid">
										{testResults.map((result, index) => {
											const tone = getTestResultTone(result);
											const isAbnormal = tone === "danger";

											return (
												<article
													className={`doctor-result-card is-${tone}`}
													key={result.id || `${result.parameter_code || "result"}-${index}`}
												>
													<div className="doctor-result-header">
														<div>
															<h3>
																{result.parameter_name || "Test Parameter"}
															</h3>

															{result.parameter_code && (
																<span className="doctor-result-code">
																	{result.parameter_code}
																</span>
															)}
														</div>

														<span
															className={`doctor-result-status ${
																result.status === "COMPLETED"
																	? "is-success"
																	: "is-neutral"
															}`}
														>
															{result.status || "-"}
														</span>
													</div>

													<div className="doctor-result-value">
														<strong>{result.value || "-"}</strong>

														{result.unit && <span>{result.unit}</span>}

														{isAbnormal && (
															<span className="doctor-result-flag">
																<IconAlert />
																Review
															</span>
														)}
													</div>

													{result.remarks && (
														<p className="doctor-result-remarks">
															<strong>Remarks</strong>
															{result.remarks}
														</p>
													)}
												</article>
											);
										})}
									</div>
								)}
							</section>

							<section
								className="doctor-tab-panel"
								id="doctor-tabpanel-underwriting"
								role="tabpanel"
								aria-labelledby="doctor-tab-underwriting"
								hidden={activeTab !== "underwriting"}
							>
								<div className="doctor-tab-heading">
									<div>
										<h2>Underwriting assessment</h2>
										<p>
											Health questionnaire and AI vitals recorded before
											this consultation.
										</p>
									</div>

									{underwritingCount > 0 && (
										<span className="doctor-count-badge">
											{underwritingCount}{" "}
											{underwritingCount === 1 ? "assessment" : "assessments"}
										</span>
									)}
								</div>

								{loadingUnderwriting && (
									<div className="doctor-skeleton-grid" role="status" aria-live="polite">
										<span className="doctor-visually-hidden">
											Loading underwriting assessment…
										</span>
										{[0, 1, 2, 3].map((skeleton) => (
											<div className="doctor-skeleton-card" key={skeleton}>
												<span className="doctor-skeleton-line is-short" />
												<span className="doctor-skeleton-line is-value" />
												<span className="doctor-skeleton-line" />
											</div>
										))}
									</div>
								)}

								{underwritingError && (
									<div className="doctor-alert doctor-alert--danger" role="alert">
										<IconAlert />
										<span>{underwritingError}</span>
									</div>
								)}

								{!loadingUnderwriting &&
									!underwritingError &&
									!latestAssessment && (
										<div className="doctor-empty-state">
											<span className="doctor-empty-state-icon" aria-hidden="true">
												<IconShield />
											</span>
											<h3>No assessment available</h3>
											<p>
												No underwriting assessment has been recorded for this
												patient yet.
											</p>
										</div>
									)}

								{latestAssessment && (
									<>
										<dl className="doctor-underwriting-summary">
											<div>
												<dt>Status</dt>
												<dd>{formatDisplayValue(latestAssessment.status)}</dd>
											</div>

											<div>
												<dt>Submitted</dt>
												<dd>
													{formatDateTime(
														latestAssessment.submitted_at ||
															latestAssessment.updated_at
													)}
												</dd>
											</div>

											<div>
												<dt>Technician</dt>
												<dd>
													{latestAssessment.technician_info?.name || "-"}
												</dd>
											</div>

											<div>
												<dt>Heart rate</dt>
												<dd className={heartRateTone}>
													{heartRate ? `${heartRate} bpm` : "-"}
												</dd>
											</div>
										</dl>

										<div className="doctor-underwriting-groups">
											{patientProfile && (
												<div className="doctor-underwriting-group">
													<h4>Patient profile</h4>

													<dl className="doctor-underwriting-facts">
														<div>
															<dt>Full name</dt>
															<dd>{patientProfile.full_name || "-"}</dd>
														</div>

														<div>
															<dt>Member ID</dt>
															<dd>{patientProfile.member_id || "-"}</dd>
														</div>

														<div>
															<dt>Age</dt>
															<dd>{patientProfile.age ?? "-"}</dd>
														</div>

														<div>
															<dt>Gender</dt>
															<dd>{patientProfile.gender || "-"}</dd>
														</div>

														<div>
															<dt>Relationship</dt>
															<dd>{patientProfile.relationship || "-"}</dd>
														</div>

														<div>
															<dt>Mobile</dt>
															<dd>{patientProfile.mobile || "-"}</dd>
														</div>
													</dl>
												</div>
											)}

											{Object.entries(questionnaireData)
												.filter(
													([groupKey]) =>
														!(patientProfile && groupKey === "demographics")
												)
												.map(([groupKey, group]) =>
													renderUnderwritingGroup(groupKey, group)
												)}

											{renderUnderwritingGroup(
												"ai_vital_scan",
												underwritingVitals
											)}
										</div>
									</>
								)}
							</section>

							<section
								className="doctor-tab-panel"
								id="doctor-tabpanel-patient"
								role="tabpanel"
								aria-labelledby="doctor-tab-patient"
								hidden={activeTab !== "patient"}
							>
								<div className="doctor-tab-heading">
									<div>
										<h2>Patient information</h2>
										<p>Registered details for this consultation.</p>
									</div>
								</div>

								<section className="doctor-info-card" aria-labelledby="patient-details-heading">
									<div className="doctor-card-heading">
										<div>
											<h3 id="patient-details-heading">Member details</h3>
										</div>
									</div>

									<dl className="doctor-info-grid">
										{patientInfoItems.map((item) => (
											<div key={item.label}>
												<dt>{item.label}</dt>
												<dd>{item.value}</dd>
											</div>
										))}
									</dl>
								</section>

								<section className="doctor-info-card" aria-labelledby="patient-flags-heading">
									<div className="doctor-card-heading">
										<div>
											<h3 id="patient-flags-heading">Clinical flags</h3>
										</div>
									</div>

									<div className="doctor-flags-list">
										{allergies ? (
											<span className="doctor-flag doctor-flag--danger">
												<IconAlert />
												Allergies: {Array.isArray(allergies) ? allergies.filter(Boolean).join(", ") : allergies}
											</span>
										) : (
											<span className="doctor-flag doctor-flag--neutral">
												<IconCheck />
												No known allergies recorded
											</span>
										)}

										{criticalFlag && (
											<span className="doctor-flag doctor-flag--danger">
												<IconAlert />
												Critical flag raised
											</span>
										)}
									</div>
								</section>
							</section>
						</div>

						{notesError && (
							<div className="doctor-alert doctor-alert--danger" role="alert" aria-live="assertive">
								<IconAlert />
								<span>{notesError}</span>
							</div>
						)}

						<div className={`doctor-save-bar ${isDirty ? "is-dirty" : ""}`}>
							<div
								className={`doctor-save-state ${
									notesError
										? "is-error"
										: isDirty
											? "is-dirty"
											: savedAtLabel
												? "is-saved"
												: ""
								}`}
								aria-live="polite"
							>
								{savingNotes ? (
									<>
										<span className="doctor-spinner-sm" aria-hidden="true" />
										Saving notes…
									</>
								) : notesError ? (
									<>
										<IconAlert />
										Not saved
									</>
								) : isDirty ? (
									<>
										<span className="doctor-dirty-dot" aria-hidden="true" />
										Unsaved changes
									</>
								) : savedAtLabel ? (
									<>
										<IconCheck />
										Saved at {savedAtLabel}
									</>
								) : (
									<>
										<IconCheck />
										{notesSuccess || "All changes saved"}
									</>
								)}
							</div>

							<button
								className="doctor-button doctor-save-button"
								type="submit"
								form="doctor-notes-form"
								disabled={savingNotes || loadingNotes}
								onClick={(event) => {
									if (activeTab !== "notes") {
										event.preventDefault();
										pendingSaveRef.current = true;
										setActiveTab("notes");
									}
								}}
							>
								{savingNotes ? (
									<>
										<span className="doctor-button-spinner" aria-hidden="true" />
										Saving…
									</>
								) : (
									"Save notes"
								)}
							</button>
						</div>
					</div>
				</section>
			)}

			{showEndModal && (
				<div
					className="doctor-modal-backdrop"
					onMouseDown={(event) => {
						if (event.target === event.currentTarget) setShowEndModal(false);
					}}
				>
					<div
						className="doctor-modal"
						role="dialog"
						aria-modal="true"
						aria-labelledby="doctor-end-modal-title"
						aria-describedby="doctor-end-modal-description"
						ref={endModalRef}
						tabIndex={-1}
					>
						<span className="doctor-modal-icon" aria-hidden="true">
							<IconPhoneOff />
						</span>

						<h2 id="doctor-end-modal-title">End this consultation?</h2>

						<p id="doctor-end-modal-description">
							This will disconnect the video call with{" "}
							<strong>{patientName}</strong> and mark the consultation as
							completed.
						</p>

						{isDirty && (
							<div className="doctor-alert doctor-alert--warning" role="status">
								<IconAlert />
								<span>
									You have unsaved notes. Save them before ending the
									consultation.
								</span>
							</div>
						)}

						<div className="doctor-modal-actions">
							<button
								className="doctor-button doctor-button--secondary"
								type="button"
								onClick={() => setShowEndModal(false)}
							>
								Keep consultation
							</button>

							<button
								className="doctor-button doctor-button--danger"
								type="button"
								onClick={handleConfirmEnd}
								disabled={ending}
							>
								{ending ? (
									<>
										<span className="doctor-button-spinner" aria-hidden="true" />
										Ending…
									</>
								) : (
									"End consultation"
								)}
							</button>
						</div>
					</div>
				</div>
			)}
		</main>
	);
};

export default DoctorTeleConsultationPage;







